import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import {
  useGetCartQuery,
  useManageCartItem,
  useRemoveCartItem,
} from "@/api/AppApi/services/cart";
import {
  CartItemTypes,
  ICartItemType,
  ICartItem_Product,
  IGetCart,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import {
  ICommonProductDetails,
  ProductPreviewTypes,
  unit,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import {
  debounce,
  getById_ItemPropsToPost_Product_Event,
  getCartId,
} from "@/app/components/pages-components/cart/cart-functions";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState, useRef } from "react";
import { isSignedInAtom } from "../authAtoms";
import {
  compareAddOnsArrays,
  compareOtherInputs,
  getProductFromCart,
  getSelectedAddOnsPrice,
  isRequiredAddonsNotChoosed,
} from "./product-has-attrs-helpers/functions";
import {
  IAddOnWithQuantity,
  IAddons,
  ICartItemExtraFields,
  ISelectedBundleItems,
  IuseProductHasAttrsResult,
} from "./product-has-attrs-helpers/interfaces";

import { intersectionsArray } from "@/app/components/common-addons-builder/addon-functions";

import useProductPopup from "../useProductPopup";
import { currencyAtom } from "../currencyAtom";
import useEventTrackers from "../../utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import calculatPricesForSpecialPrices from "@/utils/functions/calculatPriceForSpecialPrices";
import { usePathname, useSearchParams } from "next/navigation";
import useSellingMode from "../useSellingMode";
import { IEventCustomData } from "@/api/AppApi/services/home/IEvent";
import { isKWCompliance } from "@/utils/functions/global-constants";
import {
  groupBundleItemsByBundleItemId,
  compareBundleItems,
} from "@/app/components/pages-components/product-details-page/bundle/functions";
import { v4 as uuidv4 } from "uuid";
import { useApiErrorPopup } from "@/hooks/useApiErrorPopup";

export const useProductHasAttrs = (
  product: ICommonProductDetails,
): IuseProductHasAttrsResult => {
  const {
    notFieldErr,
    setNotFieldErr,
    errorRef,
    report: reportCartError,
  } = useApiErrorPopup("add_to_cart_product_details");
  const FBEventIDRef = useRef<string>("");
  const getComapny = useGetCompanyQuery();
  const { enableSells } = useSellingMode();
  const show_all_product_units =
    getComapny.data?.result.theme.components.product.show_all_product_units;
  const default_product_unit_price =
    getComapny.data?.result.theme.components.product.default_product_unit_price;
  const enable_multiple_unit_add_to_cart =
    getComapny.data?.result.theme.components.cart?.enable_multiple_unit_add_to_cart;

  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const [isSignedIn] = useAtom(isSignedInAtom);
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;

  const [addedToCart, setAddedToCart] = useState(false);
  const [note, setNote] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState<number>();
  const selectedUnit = selectedUnitId
    ? product.units.find((item) => item.id === selectedUnitId)
    : null;
  const [quantity, setQuantity] = useState(
    selectedUnit?.min_quantity_in_cart ?? product?.min_quantity_in_cart ?? 1,
  );
  const [selectedAddOns, setSelectedAddOns] = useState<IAddons | undefined>({});
  const [selectedAddOnsPrice, setSelectedAddOnsPrice] = useState(0);
  const [selectedChildIndex, setSelectedChildIndex] = useState<number | null>(
    null,
  );
  //other_inputs
  const [otherInputs, setOtherInputs] = useState<object>({});

  const [selectedBundleItems, setSelectedBundleItems] = useState<
    ISelectedBundleItems[]
  >([]);
  const [selectedAttributesValues, setSelectedAttributesValues] = useState<
    number[]
  >([]);

  const [currency] = useAtom(currencyAtom);

  // Ref to store the timeout for debouncing addon changes
  const addonChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const noteChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { eventTrackers } = useEventTrackers();

  const handleSetSelectedAttributesValues = useCallback(
    (newValues: number[]) => {
      setSelectedAttributesValues((prev) => {
        if (
          prev.length === newValues.length &&
          prev.every((v, i) => v === newValues[i])
        ) {
          return prev;
        }
        return [...newValues];
      });
    },
    [],
  );

  const { handleClosePopup, popupValue } = useProductPopup();

  const { cartItemID } = popupValue ?? {};

  const cart_item_referer = searchParams.get("cart_item_referer");

  const addOns: IAddOnWithQuantity[][] = [];
  for (let key in selectedAddOns) {
    selectedAddOns[key] && addOns.push(selectedAddOns[key]);
  }

  const currentProd =
    selectedChildIndex === null || selectedChildIndex === -1
      ? product
      : product?.children[selectedChildIndex];

  const cartItemFromAddOnsAndBundle = (cartItems ?? [])?.find((item) => {
    const basicMatch =
      item.inner_id === currentProd?.id &&
      compareAddOnsArrays((item as ICartItem_Product).addons, addOns.flat()) &&
      (item.note || "") === (note || "") &&
      compareOtherInputs((item as ICartItem_Product).others?.data_fields, otherInputs) &&
      (!enable_multiple_unit_add_to_cart || (item as ICartItem_Product).others?.unit_id === selectedUnitId);

    // If it's a product type, also check bundle items
    if (item.type === CartItemTypes.product) {
      const currentBundleItems =
        groupBundleItemsByBundleItemId(selectedBundleItems);
      const itemBundleItems = item.bundle_items || [];

      return (
        basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
      );
    }

    return basicMatch;
  });

  const cartItemId =
    cartItemID !== null && cartItemID !== undefined
      ? cartItemID
      : cart_item_referer !== null && cart_item_referer !== undefined
        ? +cart_item_referer
        : cartItemFromAddOnsAndBundle?.id;
  const disablechangeAddon =
    (!!cartItemID || !!cart_item_referer) && addedToCart;

  const currentProductInCart = getProductFromCart(
    cartItems ?? [],
    currentProd,
    show_all_product_units!,
    cartItemId,
    default_product_unit_price,
  );
  const resetProduct = (justResetAddedTocart?: boolean) => {
    setAddedToCart(currentProductInCart.addedToCart);
    if (justResetAddedTocart) return;
    setSelectedAddOns(currentProductInCart.selectedAddons);
    setNote(currentProductInCart.note);
    setQuantity(currentProductInCart.quantity);
    setOtherInputs(currentProductInCart.other_inputs);
    setSelectedUnitId(currentProductInCart.unit_id);
    setSelectedBundleItems(currentProductInCart.bundle_items);
  };

  const manageCartApi = useManageCartItem({
    onSuccess: (res: IGetCart, variables) => {
      const { items, coupon } = res.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        if (product?.preview_type === ProductPreviewTypes.consultation) {
          !!popupValue?.productID && handleClosePopup();
          router.push(
            isSignedIn ? PagesPaths.userCheckout : PagesPaths.checkout,
          );
        }
        setAddedToCart(true);
        const addedItem = (items ?? []).find(
          (item) => item.inner_id === currentProd.id,
        );
        if (addedItem) {
          eventTrackers(
            EventType.ADD_CART,
            [addedItem],
            {
              currency: currency?.code,
              value: addedItem?.price,
              coupon: coupon?.code ?? "",
              discount: addedItem?.discount,
            },
            {},
            variables.event_id,
          );
        }
      }
    },
    onError: (error: any) => {
      resetProduct();
      reportCartError(error, {
        product_id: currentProd?.id ?? null,
        parent_product_id: product?.id ?? null,
        cart_item_id: cartItemId ?? null,
        quantity,
        unit_id: selectedUnitId ?? null,
        selected_child_index: selectedChildIndex ?? null,
        has_addons: addOns.length > 0,
      });
    },
  });

  const removeItemApi = useRemoveCartItem({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        setAddedToCart(false);
      }
      if (!!cart_item_referer) {
        router.push(pathName);
      }
    },
    onError: (_error) => {
      resetProduct();
    },
  });

  useEffect(() => {
    if (getCart.isFetching) return;
    resetProduct(!cartItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCart?.isFetching, currentProd?.id, cartItems, cartItemId]);
  useEffect(() => {
    setQuantity(currentProductInCart.quantity);
    setSelectedUnitId(currentProductInCart.unit_id);
  }, [currentProductInCart.unit_id, currentProductInCart.quantity]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addonChangeTimeoutRef.current) {
        clearTimeout(addonChangeTimeoutRef.current);
      }
      if (noteChangeTimeoutRef.current) {
        clearTimeout(noteChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleAddToCart = (
    extraFields: ICartItemExtraFields,
    other_inputs: any = {},
    redirectToCheckOutOnSuccess?: boolean,
    session_id?: number | string,
  ) => {
    const itemType = () => {
      switch (extraFields.type) {
        case CartItemTypes.slot:
          return {
            type: extraFields.type,
            date: extraFields.date,
            product_id: extraFields.product_id,
          };
        case CartItemTypes.consultation:
          return {
            type: extraFields.type,
            message: extraFields.message,
            files: extraFields.files,
          };
        default:
          return {
            type: extraFields.type,
            addons: addOns.flat(),
          };
      }
    };
    const basicFields = {
      cartId: undefined,
      id: currentProd?.id,
      quantity: quantity,
      note: note,
      attrs_values: intersectionsArray(
        product.attribute_ids,
        currentProd.attribute_ids,
      ), // currentProd.attribute_ids,
      unit_id: getPrise("unit_id") as number ?? selectedUnitId,
    };
    FBEventIDRef.current = uuidv4();
    manageCartApi.mutate(
      {
        ...basicFields,
        ...itemType(),
        ...other_inputs,
        bundle_items: groupBundleItemsByBundleItemId(selectedBundleItems),
        event_id: FBEventIDRef.current,
        session_id,
      },
      {
        onSuccess: () => {
          const KWC = isKWCompliance(getComapny.data?.result!);
          if (KWC || redirectToCheckOutOnSuccess) {
            router.push(
              isSignedIn ? PagesPaths.userCheckout : PagesPaths.checkout,
            );
            handleClosePopup();
            return;
          }
        },
      },
    );
  };

  const mutateQty = (
    qty: number,
    id: number,
    unit_id: number,
    cartItems: ICartItemType[],
  ) => {
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      id,
      currentProd.preview_type,
      cartItemId,
    );
    FBEventIDRef.current = uuidv4();
    manageCartApi.mutate({
      ...cartItem,
      quantity: qty,
      unit_id,
      event_id: FBEventIDRef.current,
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceMutate = useCallback(debounce(mutateQty, 500), [cartItemId]);
  const handleQuantityChange = (qty: number) => {
    if (!addedToCart) {
      setQuantity(qty > 0 ? qty : 1);
      return;
    }
    if (qty === 0) {
      const itemId =
        getCartId(cartItems ?? [], currentProd?.id, cartItemId) ?? 0;
      const item = cartItems?.find((item) => item.id === itemId);
      removeItemApi.mutate(itemId);
      if (item) {
        eventTrackers(EventType.remove_from_cart, [item], {
          discount: item.discount,
          tax: item.tax,
          value: item.price,
          currency: currency?.code,
        });
      }
      return;
    }
    setQuantity(qty);
    debounceMutate(qty, currentProd?.id, getPrise("unit_id") as number ?? selectedUnitId, cartItems);
  };

  const handleSelectedAddOnsChanged = (currentAddons: any) => {
    setSelectedAddOns(currentAddons);
    const addOns: IAddOnWithQuantity[] = [];
    for (let key in currentAddons) {
      currentAddons[key] && addOns.push(currentAddons[key]);
    }

    const cartItemFromAddOnsAndBundle = (cartItems ?? [])?.find((item) => {
      const basicMatch =
        item.inner_id === currentProd?.id &&
        compareAddOnsArrays((item as ICartItem_Product).addons, addOns.flat()) &&
        (item.note || "") === (note || "") &&
        compareOtherInputs((item as ICartItem_Product).others?.data_fields, otherInputs) &&
        (!enable_multiple_unit_add_to_cart || (item as ICartItem_Product).others?.unit_id === selectedUnitId);

      // If it's a product type, also check bundle items
      if (item.type === CartItemTypes.product) {
        const currentBundleItems =
          groupBundleItemsByBundleItemId(selectedBundleItems);
        const itemBundleItems = item.bundle_items || [];

        return (
          basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
        );
      }

      return basicMatch;
    });

    const cartItemId =
      cartItemID !== null && cartItemID !== undefined
        ? cartItemID
        : cart_item_referer !== null && cart_item_referer !== undefined
          ? +cart_item_referer
          : cartItemFromAddOnsAndBundle?.id;

    setSelectedAddOnsPrice(
      getSelectedAddOnsPrice(currentProd.addons_builder, addOns),
    );
    if (!addedToCart || !cartItemId) {
      return;
    }

    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      currentProd.id,
      currentProd.preview_type,
      cartItemId,
    );
    const addOnesChanged = !compareAddOnsArrays(
      cartItem?.addons,
      addOns.flat(),
      true,
    );
    if (addOnesChanged) {
      // Clear any existing timeout
      if (addonChangeTimeoutRef.current) {
        clearTimeout(addonChangeTimeoutRef.current);
      }

      // Set new timeout
      addonChangeTimeoutRef.current = setTimeout(() => {
        addonChangeTimeoutRef.current = null;
        FBEventIDRef.current = uuidv4();
        manageCartApi.mutate({
          ...cartItem,
          addons: addOns.flat(),
          event_id: FBEventIDRef.current,
        });
      }, 1000);
    }
  };

  const handleNoteChange = (newNote: string) => {
    setNote(newNote);

    const currentCartItemFromAddOnsAndBundle = (cartItems ?? [])?.find(
      (item) => {
        const basicMatch =
          item.inner_id === currentProd?.id &&
          compareAddOnsArrays((item as ICartItem_Product).addons, addOns.flat()) &&
          (item.note || "") === (newNote || "") &&
          compareOtherInputs((item as ICartItem_Product).others?.data_fields, otherInputs) &&
          (!enable_multiple_unit_add_to_cart || (item as ICartItem_Product).others?.unit_id === selectedUnitId);

        if (item.type === CartItemTypes.product) {
          const currentBundleItems =
            groupBundleItemsByBundleItemId(selectedBundleItems);
          const itemBundleItems = item.bundle_items || [];
          return (
            basicMatch &&
            compareBundleItems(currentBundleItems, itemBundleItems)
          );
        }
        return basicMatch;
      },
    );

    const currentCartItemId =
      cartItemID !== null && cartItemID !== undefined
        ? cartItemID
        : cart_item_referer !== null && cart_item_referer !== undefined
          ? +cart_item_referer
          : currentCartItemFromAddOnsAndBundle?.id;

    if (!addedToCart || !currentCartItemId) return;

    if (noteChangeTimeoutRef.current) {
      clearTimeout(noteChangeTimeoutRef.current);
    }
    noteChangeTimeoutRef.current = setTimeout(() => {
      noteChangeTimeoutRef.current = null;
      if (!cartItemID && !cart_item_referer) return;

      const cartItem = getById_ItemPropsToPost_Product_Event(
        cartItems ?? [],
        currentProd.id,
        currentProd.preview_type,
        currentCartItemId,
      );
      FBEventIDRef.current = uuidv4();
      manageCartApi.mutate({ ...cartItem, note: newNote, event_id: FBEventIDRef.current });
    }, 1000);
  };

  const handleOtherInputsChange = (newOtherInputs: any) => {
    const extractedInputs = newOtherInputs.other_inputs || newOtherInputs;
    setOtherInputs(extractedInputs);

    const currentCartItemFromAddOnsAndBundle = (cartItems ?? [])?.find(
      (item) => {
        const basicMatch =
          item.inner_id === currentProd?.id &&
          compareAddOnsArrays((item as ICartItem_Product).addons, addOns.flat()) &&
          (item.note || "") === (note || "") &&
          compareOtherInputs((item as ICartItem_Product).others?.data_fields, extractedInputs) &&
          (!enable_multiple_unit_add_to_cart || (item as ICartItem_Product).others?.unit_id === selectedUnitId);

        if (item.type === CartItemTypes.product) {
          const currentBundleItems =
            groupBundleItemsByBundleItemId(selectedBundleItems);
          const itemBundleItems = item.bundle_items || [];
          return (
            basicMatch &&
            compareBundleItems(currentBundleItems, itemBundleItems)
          );
        }
        return basicMatch;
      },
    );

    const currentCartItemId =
      cartItemID !== null && cartItemID !== undefined
        ? cartItemID
        : cart_item_referer !== null && cart_item_referer !== undefined
          ? +cart_item_referer
          : currentCartItemFromAddOnsAndBundle?.id;

    if (!addedToCart || !currentCartItemId) return;

    if (addonChangeTimeoutRef.current) {
      clearTimeout(addonChangeTimeoutRef.current);
    }

    addonChangeTimeoutRef.current = setTimeout(() => {
      addonChangeTimeoutRef.current = null;
      if (!cartItemID && !cart_item_referer) return;

      const cartItem = getById_ItemPropsToPost_Product_Event(
        cartItems ?? [],
        currentProd.id,
        currentProd.preview_type,
        currentCartItemId,
      );
      FBEventIDRef.current = uuidv4();
      manageCartApi.mutate({
        ...cartItem,
        ...newOtherInputs,
        event_id: FBEventIDRef.current,
      });
    }, 1000);
  };

  const getUnitPrice =
    (
      showUnit: boolean,
      units: unit[],
      selectedUnitId: number | undefined,
      product: ICommonProductDetails,
    ) =>
    (
      priceType:
        | "price"
        | "price_without_discount"
        | "discount_amount"
        | "discount_type"
        | "discount"
        | "unit_id",
    ) => {
      if (!showUnit || !units.length) {
        return calculatPricesForSpecialPrices({
          originalPrice: product.price,
          originalPriceWithoutDiscount: product.price_without_discount,
          originalDiscountAmount: product.discount_amount,
          originalDiscountType: product.discount_type,
          originalDiscount: product.discount,
          defaultProductUnitPrice: default_product_unit_price,
        })({ quantity, specialPrices: product.selling_prices })[priceType];
      } else {
        const selectedUnit = units.find(({ id }) => id === selectedUnitId)!;
        return calculatPricesForSpecialPrices({
          originalPrice: selectedUnit?.price,
          originalPriceWithoutDiscount: selectedUnit?.price_without_discount,
          originalDiscountAmount: selectedUnit?.discount_amount,
          originalDiscountType: selectedUnit?.discount_type,
          originalDiscount: selectedUnit?.discount,
          unitId: selectedUnitId,
          defaultProductUnitPrice: default_product_unit_price,
        })({ quantity, specialPrices: product.selling_prices })[priceType];
      }
    };

  const getPrise = getUnitPrice(
    show_all_product_units as boolean,
    currentProd.units,
    selectedUnitId,
    currentProd,
  );

  const subTotalPrice = !cartItemId
    ? (getPrise("price") as number) + selectedAddOnsPrice
    : (currentProductInCart?.price ?? 0);
  const shouldConfig =
    (currentProd?.is_configurable && selectedChildIndex === null) ||
    currentProd?.has_configurable_parent;
  const variantNotExist = selectedChildIndex === -1;
  const requiredAddonsNotChosen = isRequiredAddonsNotChoosed(
    currentProd?.addons_builder,
    selectedAddOns ?? {},
  );

  const currentProductQuantityInCart =
    cartItems
      ?.filter((item) => item.inner_id === currentProd.id)
      .filter((item) => item.id !== cartItemId)
      .reduce((prev, current) => prev + current.quantity, 0) ?? 0;

  const notAllBundleItemsChildrenSelected =
    selectedBundleItems.filter((item) => item.is_configurable && !item.child)
      .length > 0;

  const isDisabled = !!currentProd?.is_disable;
  const outOfStock =
    currentProd?.quantity_on_hand - currentProductQuantityInCart <= 0 ||
    (!!currentProd.min_quantity_in_cart &&
      currentProd.quantity_on_hand - currentProductQuantityInCart <
        currentProd.min_quantity_in_cart);

  const disableRegistrationEvent =
    currentProd.preview_type === ProductPreviewTypes.event &&
    !(currentProd.custom_data as IEventCustomData).enable_registration;

  const cartDisabled =
    shouldConfig ||
    variantNotExist ||
    requiredAddonsNotChosen ||
    (outOfStock && !addedToCart) ||
    (isDisabled && !addedToCart) ||
    disableRegistrationEvent ||
    notAllBundleItemsChildrenSelected ||
    !enableSells;
  // What this line may hold: stock (or the per-cart max) minus the product's OTHER cart lines.
  const maxQuantity =
    Math.min(
      currentProd?.quantity_on_hand,
      selectedUnit?.max_quantity_in_cart ??
        currentProd?.max_quantity_in_cart ??
        999,
    ) - currentProductQuantityInCart;
  const plusDisabled = quantity >= maxQuantity;

  const handleSelectUnits = (unit: unit) => {
    setSelectedUnitId(unit.id);
    if (unit?.min_quantity_in_cart && !addedToCart)
      setQuantity(unit?.min_quantity_in_cart);
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      currentProd.id,
      currentProd.preview_type,
      cartItemId,
    );
    if (addedToCart && !enable_multiple_unit_add_to_cart) {
      let quantity =
        unit?.min_quantity_in_cart &&
        cartItem.quantity < unit?.min_quantity_in_cart
          ? unit?.min_quantity_in_cart
          : cartItem.quantity;
      quantity =
        unit?.max_quantity_in_cart &&
        cartItem.quantity > unit?.max_quantity_in_cart
          ? unit?.max_quantity_in_cart
          : cartItem.quantity;
      FBEventIDRef.current = uuidv4();
      manageCartApi.mutate({
        ...cartItem,
        quantity,
        unit_id: unit.id,
        event_id: FBEventIDRef.current,
      });
    }
  };

  return {
    currentProd,
    selectedChildIndex,
    quantity,
    note,
    selectedAddOns: selectedAddOns ?? {},
    selectedAddOnsPrice,
    addedToCart,
    setSelectedChildIndex,
    handleAddToCart,
    handleNoteChange,
    handleQuantityChange,
    handleSelectedAddOnsChanged,
    subTotalPrice,
    plusDisabled,
    maxQuantity,
    shouldConfig,
    variantNotExist,
    requiredAddonsNotChosen,
    outOfStock,
    isDisabled,
    cartDisabled,
    otherInputs,
    handleOtherInputsChange,
    notFieldErr,
    errorRef,
    isCartUpdating: manageCartApi.isLoading,
    setNotFieldErr,
    handleSelectUnits,
    selectedUnitId,
    selectedUnit,
    getPrise,
    currency,
    cartItemId,
    disablechangeAddon,
    disableRegistrationEvent,
    selectedBundleItems,
    setSelectedBundleItems,
    notAllBundleItemsChildrenSelected,
    selectedAttributesValues,
    setSelectedAttributesValues: handleSetSelectedAttributesValues,
  };
};
